import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import classnames from "classnames";
import { SidebarContext } from "../Sidebar";
import SidebarMainNavigation from "../common/main-navigation/SidebarMainNavigation";
import MainSidebarPrematch from "./prematch/MainSidebarPrematch";
import MainSidebarLive from "./live/MainSidebarLive";
import MainSidebarCasino from "./casino/MainSidebarCasino";
import MainSidebarFooter from "./MainSidebarFooter";
import styles from "./main-sidebar.module.sass";

type IMainSidebarContext = {
    selectedSportId: number | null;
    setSelectedSportId: Dispatch<SetStateAction<number | null>>;
    scrollRef: React.RefObject<HTMLDivElement | null>;
};

export const MainSidebarContext = createContext<IMainSidebarContext>(null as any);

type IProps = {
    animate: boolean;
};

const MainSidebar: FC<IProps> = ({ animate }) => {
    const { viewType } = useContext(SidebarContext);

    const [selectedSportId, setSelectedSportId] = useState<number | null>(null);

    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (selectedSportId) {
            setSelectedSportId(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewType]);

    const renderView = useMemo(() => {
        let content = null;
        switch (viewType) {
            case "sport":
                content = (
                    <ErrorBoundary name={MainSidebarPrematch.name}>
                        <MainSidebarPrematch />
                    </ErrorBoundary>
                );
                break;
            case "live":
                content = (
                    <ErrorBoundary name={MainSidebarLive.name}>
                        <MainSidebarLive />
                    </ErrorBoundary>
                );
                break;
            case "casino":
                content = (
                    <ErrorBoundary name={MainSidebarCasino.name}>
                        <MainSidebarCasino />
                    </ErrorBoundary>
                );
                break;
            default:
                return null;
        }

        if (!content) {
            return null;
        }

        return (
            <>
                {content}
                <div className={styles.separator} />
            </>
        );
    }, [viewType]);

    const value = useMemo(
        () => ({ selectedSportId, setSelectedSportId, scrollRef }),
        [selectedSportId],
    );

    return (
        <div className={styles.mainSidebar} ref={scrollRef}>
            <SidebarMainNavigation />
            <div
                className={classnames(styles.content, "side-bar-main", animate && styles.animate)}
                data-testid="main-sidebar">
                <MainSidebarContext.Provider value={value}>
                    {renderView}
                    <MainSidebarFooter />
                </MainSidebarContext.Provider>
            </div>
        </div>
    );
};

export default MainSidebar;
